import React, { useEffect, useMemo, useState } from "react";
import {
  Archive,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  RefreshCcw,
  RotateCcw,
  X,
} from "lucide-react";
import { ReportsService } from "../../../services/reports.service";

const TABLES = [
  ["", "All archived tables"],
  ["activity_logs", "Activity Logs"],
  ["platform_logs", "Platform Logs"],
  ["driver_availability_audit", "Driver Availability Audit"],
  ["user_serviceability_logs", "User Serviceability Logs"],
  ["google_api_usage_events", "Google API Usage Events"],
  ["delivery_partner_location_history", "Delivery Partner Location History"],
  ["delivery_task_events", "Delivery Task Events"],
  ["short_video_user_events", "Short Video User Events"],
  ["short_video_admin_history", "Short Video Admin History"],
];

const TABLE_LABELS = Object.fromEntries(TABLES.filter(([key]) => key));

function fmtDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function fmtDay(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

function fmtBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function humanLabel(value) {
  const text = String(value || "").trim();
  if (!text) return "—";
  return text
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function StatusBadge({ value }) {
  const status = String(value || "UNKNOWN").toUpperCase();
  const cls = status.startsWith("COMPLETED") || status === "SUCCESS" || status === "AVAILABLE" || status === "DELIVERABLE"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status.includes("FAIL") || status.includes("UNAVAILABLE") || status.includes("NOT DELIVERABLE")
      ? "bg-rose-50 text-rose-700 border-rose-200"
      : "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-black ${cls}`}>{humanLabel(status)}</span>;
}

function toLocalIso(date) {
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : "";
}

function startOfIsoWeek(value) {
  const match = String(value || "").match(/^(\d{4})-W(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  const jan4 = new Date(year, 0, 4, 0, 0, 0, 0);
  const jan4Day = (jan4.getDay() + 6) % 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - jan4Day + ((week - 1) * 7));
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function buildRange({ mode, month, week, specificDate, customFrom, customTo }) {
  if (mode === "month" && /^\d{4}-\d{2}$/.test(month || "")) {
    const [year, monthNumber] = month.split("-").map(Number);
    return {
      from: toLocalIso(new Date(year, monthNumber - 1, 1, 0, 0, 0, 0)),
      to: toLocalIso(new Date(year, monthNumber, 1, 0, 0, 0, 0)),
    };
  }

  if (mode === "week") {
    const start = startOfIsoWeek(week);
    if (start) {
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      return { from: toLocalIso(start), to: toLocalIso(end) };
    }
  }

  if (mode === "date" && specificDate) {
    const start = new Date(`${specificDate}T00:00:00`);
    if (!Number.isNaN(start.getTime())) {
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      return { from: toLocalIso(start), to: toLocalIso(end) };
    }
  }

  if (mode === "range") {
    const start = customFrom ? new Date(`${customFrom}T00:00:00`) : null;
    const endBase = customTo ? new Date(`${customTo}T00:00:00`) : null;
    const end = endBase && !Number.isNaN(endBase.getTime()) ? new Date(endBase) : null;
    if (end) end.setDate(end.getDate() + 1);
    return {
      from: start && !Number.isNaN(start.getTime()) ? toLocalIso(start) : "",
      to: end ? toLocalIso(end) : "",
    };
  }

  return { from: "", to: "" };
}

function recordPeriod(row) {
  const from = row?.record_from;
  const to = row?.record_to;
  if (!from && !to) return "—";
  if (from && to && fmtDay(from) === fmtDay(to)) return fmtDay(from);
  return `${fmtDay(from)} – ${fmtDay(to)}`;
}

function compactFields(fields = []) {
  return (Array.isArray(fields) ? fields : [])
    .filter((item) => item?.label && item?.value)
    .slice(0, 3);
}

export default function ArchivedLogsReports() {
  const [tableName, setTableName] = useState("");
  const [status, setStatus] = useState("completed");
  const [periodMode, setPeriodMode] = useState("all");
  const [month, setMonth] = useState("");
  const [week, setWeek] = useState("");
  const [specificDate, setSpecificDate] = useState("");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState({ rows: [], total: 0, page: 1, limit: 20 });
  const [selectedRun, setSelectedRun] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState(20);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const range = useMemo(
    () => buildRange({ mode: periodMode, month, week, specificDate, customFrom, customTo }),
    [periodMode, month, week, specificDate, customFrom, customTo]
  );

  const resetSelection = () => {
    setSelectedRun(null);
    setPreview(null);
    setPreviewPage(1);
  };

  const loadList = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await ReportsService.dataArchives({
        page,
        limit: pageSize,
        tableName,
        status,
        from: range.from,
        to: range.to,
      });
      setList(response?.data || { rows: [], total: 0, page, limit: pageSize });
    } catch (e) {
      setError(e?.message || "Failed to load archive metadata");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadList(); }, [page, pageSize, tableName, status, range.from, range.to]);

  const loadPreview = async (run, nextPage = 1, nextLimit = previewPageSize) => {
    if (!run?.id) return;
    setSelectedRun(run);
    setPreviewPage(nextPage);
    setPreviewLoading(true);
    setError("");
    try {
      // MinIO is read only after this explicit View/page action.
      const response = await ReportsService.dataArchivePreview(run.id, {
        page: nextPage,
        limit: nextLimit,
        from: range.from,
        to: range.to,
      });
      setPreview(response?.data || null);
    } catch (e) {
      setError(e?.message || "Failed to read archived data");
    } finally {
      setPreviewLoading(false);
    }
  };

  const clearFilters = () => {
    setTableName("");
    setStatus("completed");
    setPeriodMode("all");
    setMonth("");
    setWeek("");
    setSpecificDate("");
    setCustomFrom("");
    setCustomTo("");
    setPage(1);
    resetSelection();
  };

  const listPages = Math.max(1, Math.ceil(Number(list.total || 0) / Number(list.limit || pageSize)));
  const previewPages = Math.max(1, Math.ceil(Number(preview?.total || 0) / Number(preview?.limit || previewPageSize)));
  const listRange = useMemo(() => {
    if (!list.total) return "0 records";
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, list.total);
    return `${start}-${end} of ${Number(list.total).toLocaleString()}`;
  }, [page, pageSize, list.total]);
  const previewRange = useMemo(() => {
    if (!preview?.total) return "0 records";
    const limit = Number(preview.limit || previewPageSize);
    const start = (previewPage - 1) * limit + 1;
    const end = Math.min(previewPage * limit, preview.total);
    return `${start}-${end} of ${Number(preview.total).toLocaleString()}`;
  }, [preview, previewPage, previewPageSize]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="flex items-center gap-2 text-lg font-black text-slate-700"><Archive size={19} /> Archived Logs</div>
          <p className="mt-1 text-xs font-semibold text-slate-400">Filters use archive metadata. MinIO opens only when you View archived records.</p>
        </div>
        <button onClick={() => void loadList()} className="inline-flex w-fit items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-50">
          <RefreshCcw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-slate-500"><Filter size={14} /> Find archived data</div>
          <button onClick={clearFilters} className="inline-flex items-center gap-1.5 text-[11px] font-black text-slate-400 hover:text-slate-600"><RotateCcw size={13} /> Reset</button>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="space-y-1">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">Data type</span>
            <select value={tableName} onChange={(e) => { setTableName(e.target.value); setPage(1); resetSelection(); }} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-600 outline-none">
              {TABLES.map(([key, label]) => <option key={key || "all"} value={key}>{label}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">Status</span>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); resetSelection(); }} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-600 outline-none">
              <option value="completed">Completed only</option>
              <option value="all">All statuses</option>
              <option value="failed">Failed</option>
              <option value="running">Running</option>
              <option value="COMPLETED_RECOVERED">Recovered</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">Find by</span>
            <select value={periodMode} onChange={(e) => { setPeriodMode(e.target.value); setPage(1); resetSelection(); }} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-600 outline-none">
              <option value="all">Any date</option>
              <option value="month">Month</option>
              <option value="week">Week</option>
              <option value="date">Specific date</option>
              <option value="range">Date range</option>
            </select>
          </label>
          <div className="space-y-1">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">Period</span>
            {periodMode === "month" ? <input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPage(1); resetSelection(); }} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-600 outline-none" /> : null}
            {periodMode === "week" ? <input type="week" value={week} onChange={(e) => { setWeek(e.target.value); setPage(1); resetSelection(); }} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-600 outline-none" /> : null}
            {periodMode === "date" ? <input type="date" value={specificDate} onChange={(e) => { setSpecificDate(e.target.value); setPage(1); resetSelection(); }} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-600 outline-none" /> : null}
            {periodMode === "range" ? (
              <div className="grid grid-cols-2 gap-2">
                <input aria-label="From date" type="date" value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); setPage(1); resetSelection(); }} className="min-w-0 rounded-xl border border-slate-200 px-2 py-2.5 text-xs font-bold text-slate-600 outline-none" />
                <input aria-label="To date" type="date" value={customTo} onChange={(e) => { setCustomTo(e.target.value); setPage(1); resetSelection(); }} className="min-w-0 rounded-xl border border-slate-200 px-2 py-2.5 text-xs font-bold text-slate-600 outline-none" />
              </div>
            ) : null}
            {periodMode === "all" ? <div className="flex h-[38px] items-center rounded-xl border border-dashed border-slate-200 px-3 text-xs font-bold text-slate-400"><CalendarDays size={14} className="mr-2" /> All archived dates</div> : null}
          </div>
        </div>
      </div>

      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">{error}</div> : null}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Record period</th>
              <th className="px-4 py-3">Archived / Deleted</th>
              <th className="px-4 py-3">Storage</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Archived on</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.rows?.map((row) => (
              <tr key={row.id} className="bg-white text-slate-600 hover:bg-slate-50/60">
                <td className="px-4 py-3">
                  <div className="font-black text-slate-700">{TABLE_LABELS[row.table_name] || humanLabel(row.table_name)}</div>
                  <div className="mt-1 text-[10px] font-bold text-slate-400">Retention {row.retention_days} days</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap font-bold text-slate-600">{recordPeriod(row)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="font-black text-slate-700">{Number(row.total_rows_archived || 0).toLocaleString()}</div>
                  <div className="mt-0.5 text-[10px] font-bold text-slate-400">Deleted {Number(row.total_rows_deleted || 0).toLocaleString()}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="font-bold">{fmtBytes(row.total_file_size_bytes)}</div>
                  <div className="mt-0.5 text-[10px] font-bold text-slate-400">{Number(row.parts_count || row.completed_parts || row.total_parts || 0)} part(s)</div>
                </td>
                <td className="px-4 py-3"><StatusBadge value={row.status} /></td>
                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(row.started_at)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => void loadPreview(row, 1)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#d86a78] px-3 py-2 text-[11px] font-black text-white hover:opacity-90"><Eye size={13} /> View</button>
                </td>
              </tr>
            ))}
            {!loading && !list.rows?.length ? <tr><td colSpan={7} className="px-4 py-12 text-center font-bold text-slate-400">No archived data matches these filters.</td></tr> : null}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 text-xs font-bold text-slate-400 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span>Showing {listRange}</span>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-bold text-slate-600 outline-none">
            {[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size} / page</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg border border-slate-200 bg-white p-2 disabled:opacity-40"><ChevronLeft size={15} /></button>
          <span>Page {page} / {listPages}</span>
          <button disabled={page >= listPages || loading} onClick={() => setPage((p) => Math.min(listPages, p + 1))} className="rounded-lg border border-slate-200 bg-white p-2 disabled:opacity-40"><ChevronRight size={15} /></button>
        </div>
      </div>

      {selectedRun ? (
        <div className="rounded-2xl border border-[#f4cdd2] bg-[#fffafb] p-4">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-sm font-black text-slate-700">{TABLE_LABELS[selectedRun.table_name] || humanLabel(selectedRun.table_name)}</div>
              <div className="mt-1 text-xs font-semibold text-slate-400">{recordPeriod(selectedRun)} · {Number(preview?.total || selectedRun.total_rows_archived || 0).toLocaleString()} matching records</div>
            </div>
            <button onClick={() => { setSelectedRun(null); setPreview(null); }} className="w-fit rounded-lg border border-slate-200 bg-white p-2 text-slate-400"><X size={15} /></button>
          </div>

          {previewLoading ? <div className="py-12 text-center text-xs font-bold text-slate-400">Reading only the requested archive data from MinIO…</div> : null}

          {!previewLoading && preview?.rows?.length ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Who / Subject</th>
                    <th className="px-4 py-3">Activity</th>
                    <th className="px-4 py-3">Details</th>
                    <th className="px-4 py-3">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((row) => {
                    const fields = compactFields(row.fields);
                    return (
                      <tr key={row.key} className="align-top text-slate-600">
                        <td className="px-4 py-3 whitespace-nowrap font-bold">{fmtDate(row.timestamp)}</td>
                        <td className="px-4 py-3">
                          <div className="font-black text-slate-700">{row.title || "Archived record"}</div>
                        </td>
                        <td className="px-4 py-3 font-bold">{row.subtitle || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="space-y-1.5">
                            {fields.length ? fields.map((field) => (
                              <div key={`${field.label}-${field.value}`} className="max-w-[340px]">
                                <span className="font-black text-slate-400">{field.label}: </span>
                                <span className="font-semibold text-slate-600">{field.value}</span>
                              </div>
                            )) : <span className="text-slate-400">—</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">{row.status ? <StatusBadge value={row.status} /> : <span className="text-slate-400">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {!previewLoading && preview && !preview.rows?.length ? <div className="py-10 text-center text-xs font-bold text-slate-400">No records match the selected date filter inside this archive.</div> : null}

          {preview ? (
            <div className="mt-4 flex flex-col gap-3 text-xs font-bold text-slate-400 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span>Showing {previewRange}</span>
                <select
                  value={previewPageSize}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setPreviewPageSize(next);
                    void loadPreview(selectedRun, 1, next);
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-bold text-slate-600 outline-none"
                >
                  {[20, 50, 100].map((size) => <option key={size} value={size}>{size} / page</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button disabled={previewPage <= 1 || previewLoading} onClick={() => void loadPreview(selectedRun, Math.max(1, previewPage - 1))} className="rounded-lg border border-slate-200 bg-white p-2 disabled:opacity-40"><ChevronLeft size={15} /></button>
                <span>Page {previewPage} / {previewPages}</span>
                <button disabled={previewPage >= previewPages || previewLoading} onClick={() => void loadPreview(selectedRun, Math.min(previewPages, previewPage + 1))} className="rounded-lg border border-slate-200 bg-white p-2 disabled:opacity-40"><ChevronRight size={15} /></button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
